/**
 * Song Library Track Permanent Delete — durable BulkOp client helpers.
 * sessionStorage holds only a pointer to the server bulkOpId (never the work queue).
 */

import type {
  TrackPermanentDeleteBulkOp,
  TrackPermanentDeleteBulkOpItem,
} from "@/features/tracks/tracksApi";
import type { BulkPermanentDeleteFailure } from "@/features/tracks/tracksApi";

export const TRACK_PD_BULK_TERMINAL = new Set([
  "completed",
  "completed_with_exceptions",
  "failed",
]);

export const TRACK_PD_BULK_POINTER_KEY = "kk.sa.songLibrary.trackPdBulkOp";

export type TrackPdBulkOpPointer = {
  bulkOpId: string;
  totalMatched: number;
  idempotencyKey: string;
  startedAt: string;
  /** Submitted Track IDs — used for deterministic cache patch if GET results lag. */
  trackIds?: string[];
};

export function newTrackPdBulkIdempotencyKey(): string {
  return `track-pd-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

export function writeTrackPdBulkOpPointer(pointer: TrackPdBulkOpPointer): void {
  try {
    sessionStorage.setItem(TRACK_PD_BULK_POINTER_KEY, JSON.stringify(pointer));
  } catch {
    // ignore quota / private mode
  }
}

export function readTrackPdBulkOpPointer(): TrackPdBulkOpPointer | null {
  try {
    const raw = sessionStorage.getItem(TRACK_PD_BULK_POINTER_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as TrackPdBulkOpPointer;
    if (!parsed || typeof parsed.bulkOpId !== "string" || !parsed.bulkOpId) {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

export function clearTrackPdBulkOpPointer(): void {
  try {
    sessionStorage.removeItem(TRACK_PD_BULK_POINTER_KEY);
  } catch {
    // ignore
  }
}

export function sleepMs(ms: number): Promise<void> {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

/** Create timeouts / 5xx — reconcile via same idempotency key before declaring failure. */
export function isAmbiguousBulkOpCreateError(err: unknown): boolean {
  if (!err || typeof err !== "object") return false;
  const e = err as {
    status?: number | string;
    originalStatus?: number;
    error?: string;
  };
  const status =
    typeof e.status === "number"
      ? e.status
      : typeof e.originalStatus === "number"
        ? e.originalStatus
        : null;
  if (status != null && status >= 500) return true;
  if (e.status === "FETCH_ERROR" || e.status === "TIMEOUT_ERROR") return true;
  if (e.error === "TypeError") return true;
  return false;
}

export function completedCountFromBulkOp(op: TrackPermanentDeleteBulkOp): number {
  const c = op.counters || {};
  return (c.succeeded || 0) + (c.failed || 0) + (c.skipped || 0);
}

export function bulkDeleteFailuresFromBulkOp(
  op: TrackPermanentDeleteBulkOp,
): BulkPermanentDeleteFailure[] {
  const items = Array.isArray(op.results) ? op.results : [];
  return items
    .filter((r) => r && r.status !== "succeeded")
    .filter(
      (r) =>
        r.status === "failed" ||
        r.status === "skipped" ||
        r.ok === false ||
        r.skipped === true,
    )
    .map((r: TrackPermanentDeleteBulkOpItem) => ({
      trackId: r.trackId,
      title: r.title ?? null,
      ok: false as const,
      skipped: r.status === "skipped" || r.skipped === true,
      message: r.message || "Permanent delete failed",
      retryable: r.retryable === true,
      details: r.reasonCode ? { code: r.reasonCode } : undefined,
    }));
}

export function succeededTrackIdsFromBulkOp(
  op: TrackPermanentDeleteBulkOp,
): string[] {
  const items = Array.isArray(op.results) ? op.results : [];
  return items
    .filter((r) => r && (r.status === "succeeded" || r.ok === true))
    .map((r) => r.trackId)
    .filter((id): id is string => typeof id === "string" && !!id);
}

/**
 * Prefer per-ITEM succeeded IDs. When the BulkOp is a clean full success but
 * GET results are missing/incomplete (DDB Query lag), fall back to the
 * submitted Track ID set so Song Library can still patch deterministically.
 */
export function resolveSucceededTrackIdsForCachePatch(
  op: TrackPermanentDeleteBulkOp,
  submittedIds?: string[] | null,
): string[] {
  const fromResults = succeededTrackIdsFromBulkOp(op);
  const succeeded = op.counters?.succeeded ?? 0;
  const failed = op.counters?.failed ?? 0;
  const skipped = op.counters?.skipped ?? 0;
  const requested = op.totalMatched || 0;

  if (
    fromResults.length > 0 &&
    (succeeded === 0 || fromResults.length >= succeeded)
  ) {
    return Array.from(new Set(fromResults));
  }

  const fallback = Array.from(
    new Set(
      (submittedIds || [])
        .map((id) => (typeof id === "string" ? id.trim() : ""))
        .filter((id) => id.length > 0),
    ),
  );

  if (
    fallback.length > 0 &&
    succeeded > 0 &&
    failed === 0 &&
    skipped === 0 &&
    (requested === 0 || succeeded === requested) &&
    (fallback.length === requested || fallback.length === succeeded)
  ) {
    return fallback;
  }

  return Array.from(new Set(fromResults));
}
